import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import ApiService from '@/services/ApiService';

const SESSION_KEY = 'intent_popup_shown';
const INTENT_KEY = 'user_intent';
const DELAY_MS = 3000;

type View = 'question' | 'now' | 'soon' | 'submitted';

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    variant="ghost"
    size="icon"
    className="absolute top-2 right-2 h-7 w-7 text-gray-400 hover:text-gray-600"
    onClick={onClick}
    aria-label="Close"
  >
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </Button>
);

const IntentPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [view, setView] = useState<View>('question');
  const [email, setEmail] = useState('');
  const [reminderPeriod, setReminderPeriod] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedIntent, setSubmittedIntent] = useState<'now' | 'soon'>('now');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (sessionStorage.getItem(SESSION_KEY) === 'true') return;
    } catch {
      // sessionStorage unavailable — proceed to show
    }

    const timer = setTimeout(() => setIsVisible(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    setIsClosing(true);

    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {
      // silently fail
    }

    setTimeout(() => setIsVisible(false), 300);
  }, []);

  const handleIntent = useCallback((intent: string) => {
    try {
      sessionStorage.setItem(INTENT_KEY, intent);
    } catch {
      // silently fail
    }

    if (intent === 'now') {
      setView('now');
    } else if (intent === 'soon') {
      setView('soon');
    } else {
      dismiss();
    }
  }, [dismiss]);

  const handleSubmitNow = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await ApiService.post('/intent/now', { email });
    } catch {
      // non-critical — still show success to the user
    }
    setIsSubmitting(false);
    setSubmittedIntent('now');
    setView('submitted');
    setTimeout(() => dismiss(), 3000);
  }, [email, isSubmitting, dismiss]);

  const handleSubmitSoon = useCallback(async (period: string) => {
    if (!email || isSubmitting) return;

    setReminderPeriod(period);
    setIsSubmitting(true);
    try {
      await ApiService.post('/intent/soon', { email, reminderPeriod: period });
    } catch {
      // non-critical — still show success to the user
    }
    setIsSubmitting(false);
    setSubmittedIntent('soon');
    setView('submitted');
    setTimeout(() => dismiss(), 3000);
  }, [email, isSubmitting, dismiss]);

  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, dismiss]);

  if (!isVisible || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] sm:w-80',
        'transform transition-all duration-300 ease-out',
        isClosing
          ? 'translate-y-4 opacity-0'
          : 'translate-y-0 opacity-100'
      )}
      role="dialog"
      aria-label="When do you need your CRJMC?"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 sm:p-6">
        <CloseButton onClick={dismiss} />

        {/* Question view */}
        {view === 'question' && (
          <>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 pr-6 mb-4">
              When do you need your CRJMC?
            </h2>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleIntent('now')}
                className="w-full min-h-[42px] px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Now
              </button>
              <button
                onClick={() => handleIntent('soon')}
                className="w-full min-h-[42px] px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
              >
                Soon
              </button>
              <button
                onClick={() => handleIntent('info')}
                className="w-full min-h-[42px] px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 bg-gray-100 cursor-default"
              >
                Just looking for information
              </button>
            </div>
          </>
        )}

        {/* Now view */}
        {view === 'now' && (
          <form onSubmit={handleSubmitNow}>
            <button
              type="button"
              onClick={() => setView('question')}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-2"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 pr-6 mb-2">
              Get 10% off your CRJMC
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Get it in 15 minutes, fully online.
            </p>
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent mb-3"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full min-h-[42px] px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60"
            >
              {isSubmitting ? 'Sending...' : 'Send me the discount code'}
            </button>
          </form>
        )}

        {/* Soon view */}
        {view === 'soon' && (
          <>
            <button
              type="button"
              onClick={() => setView('question')}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-2"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 pr-6 mb-2">
              We'll remind you
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              We'll email you a 10% discount code when you're ready.
            </p>
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-3"
            />
            <p className="text-xs text-gray-500 mb-2">Remind me in:</p>
            <div className="flex gap-2">
              {[
                { label: '1 week', value: '1w' },
                { label: '1 month', value: '1m' },
                { label: '3 months', value: '3m' },
              ].map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  disabled={isSubmitting || !email}
                  onClick={() => handleSubmitSoon(value)}
                  className={cn(
                    'flex-1 min-h-[42px] px-2 py-2.5 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:opacity-60',
                    'text-white bg-orange-500 hover:bg-orange-600 active:bg-orange-700'
                  )}
                >
                  {isSubmitting && reminderPeriod === value ? '...' : label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Submitted view */}
        {view === 'submitted' && (
          <div className="text-center py-2">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {submittedIntent === 'now' ? (
              <>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  Check your inbox!
                </h2>
                <p className="text-xs text-gray-500">
                  We've sent your discount code to {email}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  We'll remind you{reminderPeriod === '1w' ? ' in 1 week' : reminderPeriod === '1m' ? ' in 1 month' : ' in 3 months'}!
                </h2>
                <p className="text-xs text-gray-500">
                  We'll send a reminder with a 10% discount to {email}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default IntentPopup;
